import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Volume2,
  VolumeX,
  Trash2,
  Save,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
  Pencil,
  MoveRight,
  Play,
  Pause,
  RefreshCw,
} from "lucide-react";
import { useState, useCallback, useRef, useEffect } from "react";
import { Button, Input } from "@learn-me/ui";
import { MiniRichTextEditor } from "@/components/editor/MiniRichTextEditor";
import type { ChapterSection } from "../api/adminApi";

interface SortableSectionCardProps {
  section: ChapterSection;
  onSave: (
    sectionId: string,
    data: { title: string; content: string },
  ) => Promise<void>;
  onDelete: (sectionId: string) => void;
  onMove?: (section: ChapterSection) => void;
  onGenerateAudio: (sectionId: string) => void;
  onDeleteAudio: (sectionId: string) => void;
  isGeneratingAudio: boolean;
  isDeletingAudio: boolean;
  isSaving: boolean;
  canMove?: boolean;
}

export function SortableSectionCard({
  section,
  onSave,
  onDelete,
  onMove,
  onGenerateAudio,
  onDeleteAudio,
  isGeneratingAudio,
  isDeletingAudio,
  isSaving,
  canMove = false,
}: SortableSectionCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(section.title);
  const [editContent, setEditContent] = useState(section.content);
  const [hasChanges, setHasChanges] = useState(false);

  // Audio preview state
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAudioPreview, setShowAudioPreview] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const prevAudioUrl = useRef(section.audioUrl);

  // Show preview when audio is newly generated
  useEffect(() => {
    if (section.audioUrl && !prevAudioUrl.current) {
      setShowAudioPreview(true);
      setIsExpanded(true);
    }
    prevAudioUrl.current = section.audioUrl;
  }, [section.audioUrl]);

  // Audio playback handlers
  const handlePlayPause = useCallback(() => {
    if (!audioRef.current || !section.audioUrl) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, section.audioUrl]);

  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
    setAudioProgress(0);
  }, []);

  const handleTimeUpdate = useCallback(() => {
    if (!audioRef.current) return;
    const progress =
      (audioRef.current.currentTime / audioRef.current.duration) * 100;
    setAudioProgress(progress || 0);
  }, []);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Strip HTML tags for preview
  const contentPreview = section.content
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .substring(0, 150);

  const hasAudio = !!section.audioUrl;

  const handleStartEdit = useCallback(() => {
    setIsEditing(true);
    setIsExpanded(true);
    setEditTitle(section.title);
    setEditContent(section.content);
    setHasChanges(false);
  }, [section.title, section.content]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditTitle(section.title);
    setEditContent(section.content);
    setHasChanges(false);
  }, [section.title, section.content]);

  const handleSave = useCallback(async () => {
    await onSave(section.id, { title: editTitle, content: editContent });
    setIsEditing(false);
    setHasChanges(false);
  }, [section.id, editTitle, editContent, onSave]);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditTitle(e.target.value);
      setHasChanges(true);
    },
    [],
  );

  const handleContentChange = useCallback((html: string) => {
    setEditContent(html);
    setHasChanges(true);
  }, []);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg bg-white transition-all ${
        isDragging ? "opacity-50 shadow-lg z-50" : "opacity-100"
      } ${isEditing ? "ring-2 ring-primary-500 ring-offset-2" : ""}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 p-3 border-b bg-gray-50">
        {/* Drag handle */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 touch-none"
          disabled={isEditing}
        >
          <GripVertical className="h-5 w-5" />
        </button>

        {/* Section number */}
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-medium">
          {section.position}
        </span>

        {/* Title - editable or display */}
        {isEditing ? (
          <Input
            value={editTitle}
            onChange={handleTitleChange}
            className="flex-1 font-medium"
            placeholder="Titulo de la seccion"
          />
        ) : (
          <h4 className="flex-1 font-medium text-gray-900 truncate">
            {section.title}
          </h4>
        )}

        {/* Audio indicator */}
        {hasAudio ? (
          <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
            <Volume2 className="h-3 w-3" />
            {section.audioDuration
              ? `${Math.floor(section.audioDuration / 60)}:${(section.audioDuration % 60).toString().padStart(2, "0")}`
              : "Audio"}
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
            <VolumeX className="h-3 w-3" />
            Sin audio
          </span>
        )}

        {/* Expand/Collapse (only when not editing) */}
        {!isEditing && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {/* Content preview (collapsed, not editing) */}
      {!isExpanded && !isEditing && contentPreview && (
        <div className="px-4 py-2 text-sm text-gray-500 truncate">
          {contentPreview}...
        </div>
      )}

      {/* Content - View mode (expanded, not editing) */}
      {isExpanded && !isEditing && (
        <div className="p-4">
          <div
            className="prose prose-sm max-w-none text-gray-700"
            dangerouslySetInnerHTML={{ __html: section.content }}
          />
        </div>
      )}

      {/* Content - Edit mode */}
      {isEditing && (
        <div className="p-4">
          <MiniRichTextEditor
            content={editContent}
            onChange={handleContentChange}
            autoFocus
          />
        </div>
      )}

      {/* Audio Preview Panel */}
      {hasAudio && showAudioPreview && !isEditing && (
        <div className="mx-4 mb-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-800 flex items-center gap-1">
              <Volume2 className="h-4 w-4" />
              Audio generado - Escucha y confirma
            </span>
            <button
              type="button"
              onClick={() => setShowAudioPreview(false)}
              className="text-green-600 hover:text-green-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Audio element */}
          <audio
            ref={audioRef}
            src={section.audioUrl ?? undefined}
            onEnded={handleAudioEnded}
            onTimeUpdate={handleTimeUpdate}
            className="hidden"
          />

          {/* Custom player */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handlePlayPause}
              className="flex-shrink-0 w-10 h-10 rounded-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center transition-colors"
            >
              {isPlaying ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5 ml-0.5" />
              )}
            </button>

            {/* Progress bar */}
            <div className="flex-1 h-2 bg-green-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all duration-100"
                style={{ width: `${audioProgress}%` }}
              />
            </div>

            {/* Duration */}
            <span className="text-xs text-green-700 min-w-[40px] text-right">
              {section.audioDuration
                ? `${Math.floor(section.audioDuration / 60)}:${(section.audioDuration % 60).toString().padStart(2, "0")}`
                : "--:--"}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-green-200">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onGenerateAudio(section.id)}
              disabled={isGeneratingAudio}
              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
            >
              {isGeneratingAudio ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Regenerar
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onDeleteAudio(section.id);
                setShowAudioPreview(false);
              }}
              disabled={isDeletingAudio}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {isDeletingAudio ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <VolumeX className="h-4 w-4 mr-1" />
              )}
              Eliminar
            </Button>
            <div className="flex-1" />
            <Button
              type="button"
              size="sm"
              onClick={() => setShowAudioPreview(false)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Confirmar audio
            </Button>
          </div>
        </div>
      )}

      {/* Compact audio player when preview is dismissed */}
      {hasAudio && !showAudioPreview && !isEditing && isExpanded && (
        <div className="mx-4 mb-3 p-2 bg-gray-50 border rounded-lg flex items-center gap-3">
          <audio
            ref={audioRef}
            src={section.audioUrl ?? undefined}
            onEnded={handleAudioEnded}
            onTimeUpdate={handleTimeUpdate}
            className="hidden"
          />
          <button
            type="button"
            onClick={handlePlayPause}
            className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 hover:bg-primary-700 text-white flex items-center justify-center transition-colors"
          >
            {isPlaying ? (
              <Pause className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4 ml-0.5" />
            )}
          </button>
          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-all duration-100"
              style={{ width: `${audioProgress}%` }}
            />
          </div>
          <span className="text-xs text-gray-500">
            {section.audioDuration
              ? `${Math.floor(section.audioDuration / 60)}:${(section.audioDuration % 60).toString().padStart(2, "0")}`
              : "--:--"}
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-t bg-gray-50/50">
        {/* Left side - Audio actions */}
        <div className="flex items-center gap-2">
          {hasAudio ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onDeleteAudio(section.id)}
              disabled={isDeletingAudio || isEditing}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {isDeletingAudio ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <VolumeX className="h-4 w-4 mr-1" />
              )}
              Eliminar Audio
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onGenerateAudio(section.id)}
              disabled={isGeneratingAudio || isEditing}
              className="text-primary-600 hover:text-primary-700"
            >
              {isGeneratingAudio ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Volume2 className="h-4 w-4 mr-1" />
              )}
              Generar Audio
            </Button>
          )}
        </div>

        {/* Right side - Edit/Save/Delete actions */}
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancelEdit}
                disabled={isSaving}
                className="text-gray-600 hover:text-gray-800"
              >
                <X className="h-4 w-4 mr-1" />
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Guardar
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleStartEdit}
                className="text-gray-600 hover:text-gray-800"
              >
                <Pencil className="h-4 w-4 mr-1" />
                Editar
              </Button>
              {canMove && onMove && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onMove(section)}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  <MoveRight className="h-4 w-4 mr-1" />
                  Mover
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onDelete(section.id)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Eliminar
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
